import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { DataTable, FilterBar, Notice, SaveBar, StatusPill } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useFormDirty } from '../../hooks/useFormDirty';
import { useBeforeUnloadWarning } from '../../hooks/useBeforeUnloadWarning';
import { useConfirmDiscard } from '../../hooks/useConfirmDiscard';
import { getStockMap, recordStockCount, StockEntry } from '../../services/consumableInventoryService';
import type { ConsumableType } from '../../services/consumableService';
import { formatQuantity } from '../../utils/formatQuantity';

const ConsumableStockCountPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState<StockEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [search, setSearch] = useState('');
  const [activeType, setActiveType] = useState<ConsumableType | null>(null);
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const TYPE_CHIPS: { id: ConsumableType; labelKey: string }[] = [
    { id: 'chemical',   labelKey: 'consumables.catalog.filters.type.chemical' },
    { id: 'ppe',        labelKey: 'consumables.catalog.filters.type.ppe' },
    { id: 'fertilizer', labelKey: 'consumables.catalog.filters.type.fertilizer' },
    { id: 'diesel',     labelKey: 'consumables.catalog.filters.type.diesel' },
  ];

  const chips = TYPE_CHIPS.map(c => ({
    id: c.id,
    label: t(c.labelKey),
    active: activeType === c.id,
  }));

  const visibleProducts = products.filter(p => {
    if (activeType && p.consumableType !== activeType) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!p.productName.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('consumables.title'), to: '/consumables/catalog' },
    { label: t('consumables.stockLevels.title'), to: '/consumables/stock-levels' },
    { label: t('consumables.stockCount.title') },
  ]);

  useEffect(() => {
    getStockMap({ includeEmpty: true })
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const filledCount = Object.keys(counts).length;
  const { dirty } = useFormDirty(false, filledCount > 0);
  useBeforeUnloadWarning(dirty);
  const confirmDiscard = useConfirmDiscard();
  const nonZeroDeltaCount = products.filter(p => {
    // A unit-conflict product (currentStock: null) has no reference value to
    // diff against, so it never contributes to the delta count.
    if (p.currentStock == null) return false;
    const physical = counts[p.productId];
    return physical !== undefined && physical !== p.currentStock;
  }).length;

  const handleCountChange = (productId: string, raw: string) => {
    setCounts(prev => {
      const next = { ...prev };
      if (raw === '') {
        delete next[productId];
      } else {
        const n = Number(raw);
        if (Number.isFinite(n) && n >= 0) {
          next[productId] = n;
        }
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSubmitting(true);
    setError('');
    const payload = {
      date: new Date().toISOString().slice(0, 10),
      counts: Object.entries(counts).map(([productId, physicalQuantity]) => ({
        productId,
        physicalQuantity,
      })),
    };
    try {
      const result = await recordStockCount(payload);
      navigate('/consumables/stock-levels', {
        state: {
          flash: t('consumables.stockCount.success', { count: result.rowsWritten }),
        },
      });
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message
        || t('consumables.stockCount.errors.unknown');
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const variance = (p: StockEntry): number | undefined => {
    // A unit-conflict product has no system stock number to diff against —
    // never compute a delta for it (would produce NaN).
    if (p.currentStock == null) return undefined;
    const physical = counts[p.productId];
    if (physical === undefined) return undefined;
    return physical - p.currentStock;
  };

  if (loading) return <div>{t('common.loading', { defaultValue: 'Loading…' })}</div>;

  return (
    <div data-testid="consumables-stock-count-page">
      <PageHeader
        title={t('consumables.stockCount.title')}
        meta={t('consumables.stockCount.meta')}
        actions={
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={() => { if (confirmDiscard(dirty)) navigate('/consumables/stock-levels'); }}
          >
            {t('common.cancel')}
          </button>
        }
      />
      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        chips={chips}
        onChipClick={(id) =>
          setActiveType((prev) => (prev === id ? null : (id as ConsumableType)))
        }
      />
      <DataTable
        rows={visibleProducts}
        rowKey={(r) => r.productId}
        columns={[
          {
            key: 'product',
            label: t('consumables.stockCount.columns.product'),
            render: (r) => r.lNumber ? `${r.productName} (${r.lNumber})` : r.productName,
          },
          {
            key: 'systemStock',
            label: t('consumables.stockCount.columns.systemStock'),
            align: 'right',
            render: (r) => r.currentStock == null
              ? <StatusPill variant="warn">{t('inventory.unitConflict.badge')}</StatusPill>
              : formatQuantity(r.currentStock, r.stockUnit),
          },
          {
            key: 'physical',
            label: t('consumables.stockCount.columns.physicalCount'),
            align: 'right',
            render: (r: StockEntry) => (
              <input
                type="number"
                min="0"
                step="any"
                aria-label={`Physical count for ${r.productName}`}
                value={counts[r.productId] ?? ''}
                onChange={(e) => handleCountChange(r.productId, e.target.value)}
                placeholder="—"
                style={{ width: 80, textAlign: 'right' }}
              />
            ),
          },
          {
            key: 'variance',
            label: t('consumables.stockCount.columns.variance'),
            align: 'right',
            render: (r: StockEntry) => {
              const v = variance(r);
              if (v === undefined || v === 0) {
                return <span data-testid={`variance-cell-${r.productId}`}>—</span>;
              }
              const sign = v > 0 ? '+' : '';
              const className = v > 0 ? 'pat-notice pat-notice--success' : 'pat-notice pat-notice--warn';
              return (
                <span
                  data-testid={`variance-cell-${r.productId}`}
                  className={className}
                  style={{ display: 'inline-block', padding: '2px 6px', borderRadius: 4 }}
                >
                  {sign}{v}{r.stockUnit ? ` ${r.stockUnit}` : ''}
                </span>
              );
            },
          },
        ]}
        emptyState={t('consumables.stockCount.emptyCatalog')}
      />
      {error && (
        <div style={{ marginTop: 12 }}>
          <Notice tone="danger">
            {error}
          </Notice>
        </div>
      )}
      <SaveBar
        status={dirty ? { kind: 'dirty' } : null}
        primaryAction={{
          label: filledCount === 0
            ? t('common.save', { defaultValue: 'Save' })
            : t('consumables.stockCount.saveCount', { count: nonZeroDeltaCount }),
          onClick: handleSave,
          disabled: nonZeroDeltaCount === 0 || submitting,
        }}
        secondaryActions={[
          {
            label: t('common.cancel'),
            onClick: () => { if (confirmDiscard(dirty)) navigate('/consumables/stock-levels'); },
          },
        ]}
      />
    </div>
  );
};

export default ConsumableStockCountPage;
